import React, { useState } from "react";
import SEO from "components/SEO";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled, { css } from "styled-components/macro";
import { SectionHeading } from "components/misc/Headings";
import { PrimaryButton } from "components/misc/Buttons";
import useGetEvents from "../data/useGetEvents";
import { getAbsoluteUrl, getUrl } from "../utils/urlUtils";
import Layout from "components/layouts/Layout";
import GlobalStyles from "styles/GlobalStyles";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";

// Styled components
const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900 text-center `;
const Posts = tw.div`mt-6 sm:-mr-8 flex flex-wrap`;
const PostContainer = styled.div`
  ${tw`mt-10 w-full sm:w-1/2 lg:w-1/3 sm:pr-8`}
  ${(props) =>
    props.featured &&
    css`
      ${tw`w-full!`}
      ${Post} {
        ${tw`sm:flex-row! h-full sm:pr-4`}
      }
      ${Image} {
        ${tw`sm:h-96 sm:min-h-full sm:w-1/2 lg:w-2/3 sm:rounded-t-none sm:rounded-l-lg`}
      }
      ${Info} {
        ${tw`sm:-mr-4 sm:pl-8 sm:flex-1 sm:rounded-none sm:rounded-r-lg sm:border-t-2 sm:border-l-0`}
      }
      ${Description} {
        ${tw`text-sm mt-3 leading-loose text-gray-600 font-medium`}
      }
    `}
`;
const Post = tw.div`cursor-pointer flex flex-col bg-gray-100 rounded-lg transition-shadow hover:shadow-lg`;
const Image = styled.div`
  ${(props) =>
    css`
      background-image: url("${props.imageSrc}");
    `}
  ${tw`h-64 w-full bg-cover bg-center rounded-t-lg`}
`;
const Info = tw.div`p-8 border-2 border-t-0 rounded-lg rounded-t-none`;
const Category = tw.div`uppercase text-primary-500 text-xs font-bold tracking-widest leading-loose after:content after:block after:border-b-2 after:border-primary-500 after:w-8`;
const CreationDate = tw.div`mt-4 uppercase text-gray-600 italic font-semibold text-xs`;
const Title = tw.div`mt-1 font-black text-2xl text-gray-900 group-hover:text-primary-500 transition duration-300`;
const Description = tw.div``;

const ButtonContainer = tw.div`flex justify-center`;
const LoadMoreButton = tw(PrimaryButton)`mt-16 mx-auto`;

export default function EventsAndUpdates({
  headingText = "جامعہ کی تقاریب اور تازہ ترین خبریں",
}) {
  const PAGE_SIZE = 9;
  const [visible, setVisible] = useState(PAGE_SIZE);
  const navigate = useNavigate();

  // Fetch events from backend
  const { data: events, isLoading, isError, refetch } = useGetEvents(1, 50);

  // Only use real events from backend, no placeholders
  const eventPosts = events && events.length > 0 ? events : [];

  return (
    <Layout>
      <SEO title="Events & Updates" description="Stay updated with the latest events, programs, and news from Jamia Mohammadia Arabia, Rayadurg." path="/events" />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>{headingText}</Heading>
          </HeadingRow>
          {isLoading && (
            <div tw="text-center text-lg py-12 text-gray-600">
              Loading Events...
            </div>
          )}
          {isError && (
            <div tw="text-center text-red-600 py-12">
              Error loading events.{" "}
              <PrimaryButton onClick={refetch}>Retry</PrimaryButton>
            </div>
          )}
          <Posts>
            {eventPosts.slice(0, visible).map((post, index) => (
              <PostContainer key={index} featured={post.featured}>
                <Post
                  as="div"
                  onClick={() => navigate(`/events/${post.id}`)}
                  className="group"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      navigate(`/events/${post.id}`);
                    }
                  }}
                >
                  <Image
                    imageSrc={getAbsoluteUrl(post.eventImages[0].imageUrl)}
                    aria-label={post.title || post.name}
                    role="img"
                  />
                  <Info>
                    <Category>{post.category || "Event"}</Category>
                    {post.date && (
                      <CreationDate>
                        {formatDistanceToNow(new Date(post.date), {
                          addSuffix: true,
                        })}
                      </CreationDate>
                    )}
                    <Title>{post.title || post.name}</Title>
                    {post.featured && post.description && (
                      <Description>{post.description}</Description>
                    )}
                  </Info>
                </Post>
              </PostContainer>
            ))}
          </Posts>
          {/* Load More button */}
          {eventPosts.length > visible && (
            <ButtonContainer>
              <LoadMoreButton onClick={() => setVisible((v) => v + PAGE_SIZE)}>
                Load More Events
              </LoadMoreButton>
            </ButtonContainer>
          )}
          {/* If no events */}
          {eventPosts.length === 0 && !isLoading && (
            <div tw="text-center text-gray-600 py-12">No events found.</div>
          )}
        </ContentWithPaddingXl>
      </Container>
    </Layout>
  );
}
